import {
  PageContentContainer,
  PageLoading,
} from "@fp/ui/components/base/page-layout/page-layout";
import { Checkbox } from "@fp/ui/components/base/checkbox/checkbox";
import { useQueryClient } from "@tanstack/react-query";
import { createFileRoute } from "@tanstack/react-router";
import { toast } from "sonner";
import { AdminLayout } from "@/components/admin-layout";
import { AdminPageHeader } from "@/components/admin-page-header";
import {
  useGetNotificationPreferences,
  useUpdateNotificationMute,
  getGetNotificationPreferencesQueryKey,
} from "@fp/admin-api-client/notification-preferences/notification-preferences.client";
import type { ChannelType } from "@fp/admin-api-client/types/channelType";
import type { NotificationGroup } from "@fp/admin-api-client/types/notificationGroup";
import type { NotificationPreferencesDto } from "@fp/admin-api-client/types/notificationPreferencesDto";
import type { NotificationTypePreferenceDto } from "@fp/admin-api-client/types/notificationTypePreferenceDto";

export const Route = createFileRoute("/profile/notifications/")({
  component: RouteComponent,
  pendingComponent: () => <PageLoading />,
});

const channelLabels: Record<ChannelType, string> = {
  Email: "Email",
  Inbox: "Notification center",
  Push: "Push",
  SignalR: "In-app",
  Slack: "Slack",
  Sms: "SMS",
};

const groupLabels: Record<NotificationGroup, string> = {
  Pay: "Pay",
  Payouts: "Payouts",
  PayoutMethods: "Payout methods",
  Funding: "Funding",
  Payees: "Payees",
  Opportunities: "Opportunities",
  FieldService: "Field service",
  Documents: "Documents",
  Account: "Account",
  System: "System",
};

interface NotificationTypeSection {
  group: NotificationGroup;
  types: NotificationTypePreferenceDto[];
}

// The API returns the types already ordered by group, so a section starts wherever the group changes.
const toSections = (types: NotificationTypePreferenceDto[]) =>
  types.reduce<NotificationTypeSection[]>((sections, type) => {
    const currentSection = sections[sections.length - 1];

    if (currentSection?.group === type.group) {
      currentSection.types.push(type);
    } else {
      sections.push({ group: type.group, types: [type] });
    }

    return sections;
  }, []);

function RouteComponent() {
  const queryClient = useQueryClient();
  const queryKey = getGetNotificationPreferencesQueryKey();

  const { data: response, isLoading } = useGetNotificationPreferences();
  const preferences = response?.data;

  const updateMuteMutation = useUpdateNotificationMute({
    mutation: {
      onMutate: async ({ typeKey, channel, data }) => {
        await queryClient.cancelQueries({ queryKey });
        const previous = queryClient.getQueryData(queryKey);

        queryClient.setQueryData(queryKey, (old: { data: NotificationPreferencesDto } | undefined) =>
          old === undefined
            ? old
            : {
                ...old,
                data: {
                  ...old.data,
                  types: old.data.types.map((type) =>
                    type.key !== typeKey
                      ? type
                      : {
                          ...type,
                          mutedChannels: data.muted
                            ? [...type.mutedChannels, channel]
                            : type.mutedChannels.filter((muted) => muted !== channel),
                        },
                  ),
                },
              },
        );

        return { previous };
      },
      onError: (_error, _variables, context) => {
        queryClient.setQueryData(queryKey, context?.previous);
        toast.error("Failed to update notification preference");
      },
      onSettled: () => {
        queryClient.invalidateQueries({ queryKey });
      },
    },
  });

  const handleToggle = (typeKey: string, channel: ChannelType, enabled: boolean) => {
    updateMuteMutation.mutate({ typeKey, channel, data: { muted: !enabled } });
  };

  return (
    <AdminLayout>
      <AdminPageHeader
        title="Notifications"
        parent={{ url: "/profile", label: "Profile" }}
      />
      <PageContentContainer className="overflow-y-hidden">
        <p className="text-gray-600 mb-6 shrink-0">
          Choose which notifications reach you on each channel. Turning a whole channel off is done
          from your profile.
        </p>
        {isLoading ? (
          <p className="text-gray-600">Loading notifications...</p>
        ) : preferences === undefined || preferences.types.length === 0 ? (
          <p className="text-gray-600">There are no notifications to configure.</p>
        ) : (
          <div className="flex-1 min-h-0 overflow-auto space-y-6">
            {toSections(preferences.types).map((section) => (
              <section
                key={section.group}
                className="bg-white border border-gray-200 rounded-lg overflow-hidden"
              >
                <table className="w-full table-fixed text-left">
                  <colgroup>
                    <col />
                    {preferences.channels.map((channel) => (
                      <col key={channel} className="w-28" />
                    ))}
                  </colgroup>
                  <thead className="bg-gray-50">
                    <tr className="border-b border-gray-200">
                      <th className="px-4 py-3 text-sm font-semibold text-gray-900">
                        {groupLabels[section.group]}
                      </th>
                      {preferences.channels.map((channel) => (
                        <th
                          key={channel}
                          className="px-4 py-3 text-sm font-semibold text-gray-600 text-center whitespace-nowrap"
                        >
                          {channelLabels[channel]}
                        </th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {section.types.map((type) => (
                      <tr key={type.key} className="border-b border-gray-100 last:border-b-0">
                        <td className="p-4">
                          <div className="text-sm font-medium text-gray-900">{type.displayName}</div>
                          <div className="text-sm text-gray-600">{type.description}</div>
                        </td>
                        {preferences.channels.map((channel) => {
                          const isDeclared = type.channels.includes(channel);
                          const isEnabled = !type.mutedChannels.includes(channel);

                          return (
                            <td key={channel} className="p-4 text-center">
                              {isDeclared ? (
                                <Checkbox
                                  aria-label={`${type.displayName} via ${channelLabels[channel]}`}
                                  isSelected={isEnabled}
                                  onChange={(value: boolean) => handleToggle(type.key, channel, value)}
                                />
                              ) : (
                                <span className="text-gray-300">—</span>
                              )}
                            </td>
                          );
                        })}
                      </tr>
                    ))}
                  </tbody>
                </table>
              </section>
            ))}
          </div>
        )}
      </PageContentContainer>
    </AdminLayout>
  );
}